import React, { useRef, useState, useEffect, useCallback } from 'react';
import { motion } from 'framer-motion';
import { Play, Pause, Volume2, VolumeX } from 'lucide-react';
import { useTranslation } from 'react-i18next';

const videos = [
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/m0mznr33_wnba_x_ovo%20%281080p%29.mp4', title: 'WNBA x OVO', tag: 'Sports' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/krjrb7av_honda_x_lou_pascal%20%281080p%29.mp4', title: 'Honda x Lou Pascal', tag: 'Automotive' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/rbeyd4bu_georges_saint-pierre_for_ufc_x_ovo%20%281080p%29.mp4', title: 'GSP for UFC x OVO', tag: 'Sports' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/z8qfo8c1_nespresso_x_lou-pascal_tremblay%20%281080p%29.mp4', title: 'Nespresso x Lou-Pascal', tag: 'Lifestyle' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/yhevu75l_lamarque_iconic%20%281080p%29.mp4', title: 'Lamarque Iconic', tag: 'Fashion' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/yfka5901_blacksuede%20%281080p%29.mp4', title: 'Black Suede', tag: 'Fashion' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/0dpj6h3k_ovo_x_roy_woods%20%282160p%29.mp4', title: 'OVO x Roy Woods', tag: 'Music' },
  { src: 'https://customer-assets.emergentagent.com/job_9803f995-e646-4ca3-9c16-5e6a4e044236/artifacts/9oeogjqb_ovo_campus%20%281080p%29.mp4', title: 'OVO Campus', tag: 'Lifestyle' },
];

const VideoCard = ({ video, index }) => {
  const videoRef = useRef(null);
  const containerRef = useRef(null);
  const [playing, setPlaying] = useState(false);
  const [muted, setMuted] = useState(true);
  const [isVisible, setIsVisible] = useState(false);
  const [loaded, setLoaded] = useState(false);

  useEffect(() => {
    const el = containerRef.current;
    if (!el) return;
    const observer = new IntersectionObserver(([entry]) => { if (entry.isIntersecting) { setIsVisible(true); observer.disconnect(); } }, { rootMargin: '200px' });
    observer.observe(el);
    return () => observer.disconnect();
  }, []);

  useEffect(() => {
    const el = containerRef.current;
    if (!el || !playing) return;
    const observer = new IntersectionObserver(([entry]) => { if (!entry.isIntersecting && videoRef.current) { videoRef.current.pause(); setPlaying(false); } }, { threshold: 0.1 });
    observer.observe(el);
    return () => observer.disconnect();
  }, [playing]);

  const toggle = useCallback(() => { if (!videoRef.current) return; if (playing) { videoRef.current.pause(); } else { videoRef.current.play(); } setPlaying(p => !p); }, [playing]);
  const toggleMute = useCallback((e) => { e.stopPropagation(); if (!videoRef.current) return; videoRef.current.muted = !muted; setMuted(m => !m); }, [muted]);

  return (
    <motion.div ref={containerRef} initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} transition={{ delay: index * 0.08 }} data-testid={`video-card-${index}`} className="group relative rounded-xl overflow-hidden cursor-pointer bg-white/[0.02] border border-white/5" onClick={toggle}>
      <div className="relative aspect-video bg-gray-900">
        {isVisible ? <video ref={videoRef} src={video.src} muted={muted} loop playsInline preload="none" onLoadedData={() => setLoaded(true)} className="w-full h-full object-cover" /> : null}
        {!loaded && (
          <div className="absolute inset-0 bg-gray-900 flex items-center justify-center">
            <div className="text-center"><div className="w-10 h-10 rounded-full border-2 border-[#c8ff00]/30 border-t-[#c8ff00] animate-spin mx-auto mb-2" /><p className="text-gray-500 text-xs">{video.title}</p></div>
          </div>
        )}
        <div className={`absolute inset-0 flex items-center justify-center transition-opacity duration-300 ${playing ? 'opacity-0 group-hover:opacity-100' : 'opacity-100'} bg-black/30`}>
          <div className="w-14 h-14 rounded-full bg-[#c8ff00] flex items-center justify-center">{playing ? <Pause className="w-6 h-6 text-black" /> : <Play className="w-6 h-6 text-black ml-0.5" />}</div>
        </div>
        {playing && (
          <button onClick={toggleMute} className="absolute bottom-3 right-3 w-8 h-8 rounded-full bg-black/60 flex items-center justify-center hover:bg-black/80 transition-colors z-10">
            {muted ? <VolumeX className="w-4 h-4 text-white" /> : <Volume2 className="w-4 h-4 text-white" />}
          </button>
        )}
      </div>
      <div className="p-4"><div className="flex items-center justify-between"><h4 className="text-white font-bold font-['Syne'] text-sm">{video.title}</h4><span className="text-[#c8ff00]/70 text-xs font-bold uppercase tracking-wider">{video.tag}</span></div></div>
    </motion.div>
  );
};

const VideoShowcase = () => {
  const { t } = useTranslation();
  return (
    <section data-testid="video-showcase" className="py-16 md:py-24 border-t border-white/5">
      <div className="container-custom">
        <motion.div initial={{ opacity: 0, y: 20 }} whileInView={{ opacity: 1, y: 0 }} viewport={{ once: true }} className="text-center mb-12">
          <p className="text-[#c8ff00] text-xs font-bold uppercase tracking-[0.2em] mb-3">{t('home.videoShowcase.tag')}</p>
          <h2 className="text-3xl md:text-4xl font-bold font-['Syne'] text-white">{t('home.videoShowcase.title')}</h2>
        </motion.div>
        <div className="grid grid-cols-1 md:grid-cols-2 lg:grid-cols-3 gap-5">{videos.slice(0, 6).map((v, i) => <VideoCard key={v.title} video={v} index={i} />)}</div>
        <div className="grid grid-cols-1 md:grid-cols-2 gap-5 mt-5 max-w-4xl mx-auto">{videos.slice(6).map((v, i) => <VideoCard key={v.title} video={v} index={i + 6} />)}</div>
      </div>
    </section>
  );
};

export default VideoShowcase;
